"use client"

import type { PlatformMetricsMonth } from "@repo/types"
import { AlertTriangle, Ban, CalendarClock, Euro, Gauge, Sprout, TrendingUp, UserCheck, Users } from "lucide-react"
import { Bar, BarChart, CartesianGrid, Line, LineChart, XAxis, YAxis } from "recharts"
import { DendromapLoader } from "../../components/dendromap-loader"
import { Alert, AlertDescription } from "../../components/ui/alert"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card"
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from "../../components/ui/chart"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table"
import { usePlatformMetrics } from "../../hooks/api/superadmin/use-superadmin-client"
import { useLabels } from "../../hooks/use-labels"
import { PageHeader } from "../../ui/layout/page-header"
import { MetricCard } from "../dashboard/components/metric-card"
import { apiErrorMessage } from "./client-detail.helpers"
import { formatMoney, formatPercent, monthOverMonth, SERIES_COLOR, shortMonth } from "./metrics.helpers"

type MonthlyKey = Exclude<keyof PlatformMetricsMonth, "month">

const MONTHLY_SERIES: Array<{ key: MonthlyKey; title: string; description: string }> = [
  { key: "newClients", title: "Nouveaux clients", description: "Organisations créées" },
  { key: "newPayingClients", title: "Nouveaux abonnés payants", description: "Date de souscription, hors gratuit" },
  { key: "inventaires", title: "Inventaires créés", description: "Hors inventaires supprimés" },
  { key: "newUsers", title: "Nouveaux utilisateurs", description: "Comptes créés" },
  { key: "aiAnalyses", title: "Analyses IA", description: "Analyses photo facturées" },
]

/** Espace /superadmin : indicateurs de la plateforme (revenu, clients, activité). */
export function MetricsPage() {
  const { labels } = useLabels()
  const { data: report, isLoading, error } = usePlatformMetrics()

  if (isLoading) return <DendromapLoader message="Calcul des indicateurs..." />
  if (error || !report) {
    return <Alert variant="destructive"><AlertDescription>{apiErrorMessage(error, "Indicateurs indisponibles.")}</AlertDescription></Alert>
  }
  const { current, months, history } = report
  const tiles = [
    { title: "MRR estimé", value: formatMoney(current.mrr), description: `ARR ${formatMoney(current.mrr * 12)}`, icon: Euro },
    { title: "Clients payants", value: current.payingClients, description: `${formatMoney(current.arpa)} en moyenne / mois`, icon: Users },
    {
      title: "Impayés",
      value: current.pastDueClients,
      description: `${formatMoney(current.mrrAtRisk)} de MRR à risque`,
      icon: AlertTriangle,
      tone: current.pastDueClients > 0 ? "warning" : undefined,
    },
    {
      title: "Conversion des inscriptions",
      value: formatPercent(current.signupConversion.ratio),
      description: `${current.signupConversion.converted} / ${current.signupConversion.cohort} inscrits il y a 1 à 6 mois`,
      icon: TrendingUp,
    },
    { title: "Essais et gratuits", value: current.trialClients, icon: Sprout },
    { title: "Clients actifs (30 j)", value: current.activeClients30Days, description: "Au moins un inventaire créé", icon: UserCheck },
    { title: "Échéances sous 30 jours", value: current.expiringWithin30Days, icon: CalendarClock },
    { title: "Suspendus / résiliés", value: `${current.suspendedClients} / ${current.cancelledClients}`, description: `${current.totalClients} clients au total`, icon: Ban },
  ] as const

  return (
    <div className="space-y-6">
      <PageHeader>
        <PageHeader.Title>{labels.sidebar.superadmin.metrics}</PageHeader.Title>
        <PageHeader.Description>
          Revenu estimé à partir des paliers des clients (Tarifs &amp; abonnements), clients par statut et activité des 12
          derniers mois. Mis à jour le {new Date(report.generatedAt).toLocaleString("fr-FR")}.
        </PageHeader.Description>
      </PageHeader>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {tiles.map((tile) => (
          <MetricCard
            key={tile.title}
            title={tile.title}
            value={tile.value}
            description={"description" in tile ? tile.description : undefined}
            icon={tile.icon}
            iconColor={"tone" in tile && tile.tone === "warning" ? "text-foreground" : "text-primary"}
            iconBgColor={"tone" in tile && tile.tone === "warning" ? "bg-warning-muted" : "bg-primary/10"}
          />
        ))}
      </div>

      <MrrHistoryCard history={history} />

      <div className="grid gap-4 lg:grid-cols-2">
        {MONTHLY_SERIES.map((series) => (
          <MonthlyBarCard key={series.key} title={series.title} description={series.description} months={months} dataKey={series.key} />
        ))}
      </div>

      <MonthlyTable months={months} />
    </div>
  )
}

const AXIS_TICK = { fontSize: 12 }

const singleSeriesConfig = (label: string): ChartConfig => ({ value: { label, color: SERIES_COLOR } })

function MonthlyBarCard({ title, description, months, dataKey }: { title: string; description: string; months: PlatformMetricsMonth[]; dataKey: MonthlyKey }) {
  const data = months.map((m) => ({ month: shortMonth(m.month), value: m[dataKey] }))
  const trend = monthOverMonth(months.map((m) => m[dataKey]))
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{title}</CardTitle>
        <CardDescription>
          {description}
          {trend ? ` · ${trend}` : ""}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ChartContainer config={singleSeriesConfig(title)} className="h-[220px]">
          <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barCategoryGap={4}>
            <CartesianGrid vertical={false} />
            <XAxis dataKey="month" tickLine={false} axisLine={false} tickMargin={8} interval="preserveStartEnd" tick={AXIS_TICK} />
            <YAxis allowDecimals={false} tickLine={false} axisLine={false} width={36} tick={AXIS_TICK} />
            <ChartTooltip cursor={{ fillOpacity: 0.4 }} content={<ChartTooltipContent />} />
            <Bar dataKey="value" fill="var(--color-value)" radius={[4, 4, 0, 0]} maxBarSize={28} />
          </BarChart>
        </ChartContainer>
      </CardContent>
    </Card>
  )
}

function MrrHistoryCard({ history }: { history: Array<{ month: string; day: string; mrr: number }> }) {
  const data = history.map((point) => ({ month: shortMonth(point.month), value: point.mrr }))
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Évolution du MRR</CardTitle>
        <CardDescription>
          Dernière valeur de chaque mois. L&apos;historique est enregistré chaque nuit depuis la mise en service de cette page : il
          se complète au fil des mois.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {data.length < 2 ? (
          <div className="flex h-[180px] flex-col items-center justify-center gap-2 text-center">
            <Gauge className="h-6 w-6 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              {data.length === 1 ? `Premier relevé : ${formatMoney(history[0]!.mrr)} (${history[0]!.day}). ` : ""}
              La courbe apparaîtra à partir du deuxième mois de relevés.
            </p>
          </div>
        ) : (
          <ChartContainer config={singleSeriesConfig("MRR")} className="h-[240px]">
            <LineChart data={data} margin={{ top: 8, right: 16, left: 8, bottom: 0 }}>
              <CartesianGrid vertical={false} />
              <XAxis dataKey="month" tickLine={false} axisLine={false} tickMargin={8} tick={AXIS_TICK} />
              <YAxis tickLine={false} axisLine={false} width={56} tick={AXIS_TICK} tickFormatter={(v: number) => formatMoney(v)} />
              <ChartTooltip content={<ChartTooltipContent formatter={(value) => formatMoney(Number(value))} />} />
              <Line dataKey="value" stroke="var(--color-value)" strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 5 }} />
            </LineChart>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  )
}

/** Vue tableau des séries mensuelles (accessibilité, export à la main). */
function MonthlyTable({ months }: { months: PlatformMetricsMonth[] }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Données mensuelles</CardTitle>
        <CardDescription>Le mois en cours est partiel.</CardDescription>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Mois</TableHead>
              {MONTHLY_SERIES.map((series) => <TableHead key={series.key} className="text-right">{series.title}</TableHead>)}
            </TableRow>
          </TableHeader>
          <TableBody>
            {[...months].reverse().map((month) => (
              <TableRow key={month.month}>
                <TableCell className="font-medium">{shortMonth(month.month)}</TableCell>
                {MONTHLY_SERIES.map((series) => <TableCell key={series.key} className="text-right">{month[series.key]}</TableCell>)}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  )
}
